// src/components/ceremony/GridLockCeremony.js
// ============================================================================
// Founder sequence step 3 (tail) / step 4 lead-in: the field is complete, the
// grid locks, and the screen holds until the feeds take over.
//
// Ported from the approved design (Media/ShowdownDemo/assets/loading.html) —
// founder's own idea: "CritCash logo with Crits loading into user pole
// positions". A green King Crit die is thrown from the crown into each racer's
// socket; the slot flashes gold and stamps their entry; the pool counts up;
// GRID LOCKED lands when the last one seats.
//
// Unlike the demo (a fixed 16-slot script), the grid here is THIS event's
// field, in its real grid order, at its real entry cost.
// ============================================================================

import React, { useMemo } from 'react';
import { View, Text, Image, StyleSheet } from 'react-native';

import { CEREMONY_COLORS as C } from '../../constants/ceremony';
import CeremonyCanvas, {
  CeremonyHeader, anim, IS_WEB, DIE_GREEN_20, resolveSrc,
} from './CeremonyCanvas';

// --- design geometry (1920x1080 canvas) ------------------------------------
const GRID_TOP = 372;
const GRID_W = 1210;
const GRID_LEFT = (1920 - GRID_W) / 2;
// Vertical budget for the grid: the footer (progress bar + stats) sits at
// design-Y ~986, so the field has to finish above that. At 16 racers this still
// yields exactly the approved 70px row pitch; a 50-player battle royale
// compresses into it instead of running under the bar.
const GRID_H = 600;
const COL_GAP = 70;
const SLOT_H = 56;
// Below this pitch a slot stops being readable on a stream.
const MIN_ROW_PITCH = 34;
const CROWN = { x: 960, y: 165 };   // where the dice are thrown from

// --- timeline (ms from phase entry) ----------------------------------------
const T_FLIGHT_BASE = 1150;
const FLIGHT_MS = 480;
const STAMP_AT = 6250;

export default function GridLockCeremony({
  title,
  strapline,
  field = [],
  entryCrits,
  phaseElapsedMs = 0,
}) {
  const n = field.length;

  const layout = useMemo(() => {
    const cols = n <= 16 ? 2 : n <= 36 ? 3 : 4;
    const slotW = (GRID_W - COL_GAP * (cols - 1)) / cols;
    // The demo staggers the right-hand column down a half-step, F1 grid style —
    // which costs a row of height, so budget for it before dividing.
    const stagger = cols === 2 ? 35 : 0;

    // How many slots can be drawn and still be readable on a stream. A 16-up
    // keeps the approved 70px pitch; 64 (today's live battle-royale ceiling)
    // still fits every player; a 150-up Warzone lobby cannot — so draw what
    // fits and say how many are off-screen, rather than stacking rows on top
    // of each other.
    const maxRows = Math.max(1, Math.floor((GRID_H - stagger) / MIN_ROW_PITCH));
    const capacity = maxRows * cols;
    const drawn = Math.min(n, capacity);

    const rows = Math.max(1, Math.ceil(drawn / cols));
    const rowH = Math.min(70, (GRID_H - stagger) / rows);
    const slotH = Math.min(SLOT_H, Math.max(20, rowH - 14));
    // Likewise the throw cadence: the whole sequence still has to land before
    // the LOCKED IN stamp.
    const step = Math.max(24, Math.min(280, 4480 / Math.max(drawn, 1)));
    return { cols, slotW, rows, rowH, slotH, stagger, step, drawn, hidden: n - drawn };
  }, [n]);

  const shown = field.slice(0, layout.drawn);
  const landTimeFor = (i) => T_FLIGHT_BASE + i * layout.step + FLIGHT_MS;
  // The counter and bar always speak for the WHOLE field, drawn or not.
  const landedDrawn = shown.filter((_, i) => phaseElapsedMs >= landTimeFor(i)).length;
  const landed = layout.drawn
    ? Math.round((landedDrawn / layout.drawn) * n)
    : 0;
  const poolSoFar = entryCrits ? landed * entryCrits : 0;
  const stamped = phaseElapsedMs >= STAMP_AT;

  return (
    <CeremonyCanvas backdrop="grid">
      <CeremonyHeader
        title={title || 'CRITCASH EVENT'}
        subtitle={strapline ? `${strapline}  ·  LOCKING IN` : 'LOCKING IN'}
        logoWidth={236}
        top={18}
      />

      <View style={styles.grid} pointerEvents="none">
        {shown.map((r, i) => {
          const col = i % layout.cols;
          const row = Math.floor(i / layout.cols);
          const left = col * (layout.slotW + COL_GAP);
          const top = row * layout.rowH + col * layout.stagger;
          const t0 = T_FLIGHT_BASE + i * layout.step;
          const isLanded = phaseElapsedMs >= landTimeFor(i);

          // Socket centre in canvas space -> the die's flight vector.
          const socketX = GRID_LEFT + left + layout.slotW - 18 - 20;
          const socketY = GRID_TOP + top + layout.slotH / 2;

          return (
            <GridSlot
              key={r.uid || i}
              index={i}
              slot={r.slot || i + 1}
              name={r.ign || r.name}
              entryCrits={entryCrits}
              width={layout.slotW}
              height={layout.slotH}
              left={left}
              top={top}
              t0={t0}
              landed={isLanded}
              dx={socketX - CROWN.x}
              dy={socketY - CROWN.y}
            />
          );
        })}
      </View>

      <View style={[styles.footer, anim('fadeDown', { delay: 300, duration: 900 })]}>
        <View style={styles.bar}>
          <View style={[styles.fill, { width: `${n ? (landed / n) * 100 : 0}%` }]} />
        </View>
        <Text style={styles.stats}>
          <Text style={styles.statsNum}>{landed}</Text>
          {`/${n} LOCKED IN`}
          {entryCrits ? (
            <>
              {'   ·   POOL '}
              <Text style={styles.statsNum}>{poolSoFar.toLocaleString('en-US')}</Text>
              {' CRITS'}
            </>
          ) : null}
        </Text>
      </View>

      {stamped && (
        <>
          <View style={[styles.veil, anim('fadeIn', { duration: 420 })]} pointerEvents="none" />
          <View style={styles.stampRow} pointerEvents="none">
            <View
              style={[styles.stampBox, anim('stampIn', { duration: 420, easing: 'cubic-bezier(.15,1.3,.3,1)' })]}
            >
              <Text style={styles.stamp}>LOCKED IN</Text>
            </View>
          </View>
        </>
      )}

      {/* A 150-player lobby can't draw every slot legibly — say what's off
          screen instead of just ending the list. */}
      {layout.hidden > 0 && !stamped && (
        <Text style={styles.truncated}>{`+ ${layout.hidden} MORE LOCKING IN`}</Text>
      )}

      {/* No sponsor slot on this beat: it's a ~6 second transition with no
          spare real estate once a full field is on screen. The inventory lives
          on STARTING SOON and the results ceremony, which hold. */}
    </CeremonyCanvas>
  );
}

function GridSlot({ slot, name, entryCrits, width, height, left, top, t0, landed, dx, dy }) {
  const tight = height < 46;
  return (
    <>
      <View
        style={[
          styles.slot,
          { width, height, left, top },
          anim('fadeUp', { delay: 500 + slot * 30, duration: 380 }),
        ]}
      >
        <View style={[styles.pos, tight && styles.posTight]}>
          <Text style={[styles.posText, tight && styles.posTextTight]}>P{slot}</Text>
        </View>
        <Text style={[styles.ign, tight && styles.ignTight]} numberOfLines={1}>{name}</Text>
        {/* A big field has no room for the entry chip alongside the handle. */}
        {landed && entryCrits && !tight ? (
          <Text style={[styles.entry, anim('fadeUp', { duration: 300 })]}>
            {`-${entryCrits} ENTRY`}
          </Text>
        ) : null}
        <View style={[styles.socket, tight && styles.socketTight]}>
          {landed && (
            <Image
              source={DIE_GREEN_20}
              style={[styles.socketDie, anim('popIn', { duration: 200, easing: 'cubic-bezier(.2,1.4,.4,1)' })]}
              resizeMode="contain"
            />
          )}
        </View>
        {landed && (
          <View
            style={[styles.lockflash, anim('lockFlash', { duration: 500 })]}
            pointerEvents="none"
          />
        )}
      </View>

      {/* The thrown die. Web-only motion; on native the socket die simply
          appears, which reads fine and is what reduced-motion gets too. */}
      {IS_WEB && !landed && (
        <img
          src={resolveSrc(DIE_GREEN_20)}
          alt=""
          className="cc-cer-fly"
          style={{
            left: CROWN.x - GRID_LEFT - 28,
            top: CROWN.y - GRID_TOP - 28,
            width: 56,
            height: 56,
            pointerEvents: 'none',
            '--cc-dx': `${dx}px`,
            '--cc-dy': `${dy}px`,
            animationDelay: `${t0}ms`,
            animationDuration: `${FLIGHT_MS + 60}ms`,
            animationTimingFunction: 'cubic-bezier(.3,.1,.35,1)',
          }}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  grid: {
    position: 'absolute',
    top: GRID_TOP,
    left: GRID_LEFT,
    width: GRID_W,
    height: GRID_H,
  },
  slot: {
    position: 'absolute',
    borderRadius: 10,
    backgroundColor: 'rgba(17,17,17,0.92)',
    borderWidth: 1,
    borderColor: C.BORDER,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingLeft: 12,
    paddingRight: 18,
  },
  pos: {
    width: 54,
    height: 40,
    borderRadius: 8,
    backgroundColor: '#1a1a1a',
    borderWidth: 1,
    borderColor: '#333333',
    alignItems: 'center',
    justifyContent: 'center',
  },
  posTight: { width: 40, height: 26 },
  posText: { fontSize: 21, fontWeight: '900', color: C.GOLD, letterSpacing: 1 },
  posTextTight: { fontSize: 15 },
  ign: { flex: 1, fontSize: 23, fontWeight: '800', letterSpacing: 1, color: '#e8e8e8' },
  ignTight: { fontSize: 16 },
  socketTight: { width: 26, height: 26 },
  entry: { fontSize: 17, fontWeight: '800', color: C.GREEN, letterSpacing: 1 },
  socket: {
    width: 40,
    height: 40,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#333333',
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  socketDie: { width: 32, height: 32 },
  lockflash: {
    position: 'absolute',
    top: -1,
    left: -1,
    right: -1,
    bottom: -1,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: C.GOLD,
    ...(IS_WEB
      ? { boxShadow: '0 0 22px rgba(252,208,0,0.5), inset 0 0 18px rgba(252,208,0,0.18)' }
      : {}),
  },
  truncated: {
    position: 'absolute',
    bottom: 118,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 15,
    fontWeight: '800',
    letterSpacing: 4,
    color: '#6a6a6a',
  },
  footer: {
    position: 'absolute',
    left: (1920 - 900) / 2,
    bottom: 44,
    width: 900,
    alignItems: 'center',
  },
  bar: {
    height: 12,
    width: '100%',
    borderRadius: 6,
    backgroundColor: '#141414',
    borderWidth: 1,
    borderColor: '#2a2a2a',
    overflow: 'hidden',
  },
  fill: {
    height: '100%',
    backgroundColor: C.GOLD,
    ...(IS_WEB
      ? {
        backgroundImage: 'linear-gradient(90deg, #8a6d00, #FCD000 60%, #ffe565)',
        boxShadow: '0 0 18px rgba(252,208,0,0.55)',
      }
      : {}),
  },
  stats: {
    marginTop: 12,
    fontSize: 20,
    fontWeight: '800',
    letterSpacing: 3,
    color: '#cfcfcf',
  },
  statsNum: { color: C.GOLD },
  veil: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.62)',
  },
  stampRow: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 578,
    alignItems: 'center',
  },
  stampBox: {
    paddingHorizontal: 66,
    paddingVertical: 18,
    borderWidth: 4,
    borderColor: C.GOLD,
    borderRadius: 14,
    backgroundColor: 'rgba(0,0,0,0.82)',
    ...(IS_WEB
      ? { boxShadow: '0 0 60px rgba(252,208,0,0.35), inset 0 0 34px rgba(252,208,0,0.12)' }
      : {}),
  },
  stamp: {
    fontSize: 64,
    fontWeight: '900',
    letterSpacing: 14,
    color: C.GOLD,
    ...(IS_WEB ? { textShadow: '0 0 34px rgba(252,208,0,0.6)' } : {}),
  },
});
